import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "../apiAdmin";
import { HealthData } from "./admin-health/types";
import { fmtDay, fmtMonth, fmtMoneyMap, IncidentTable, RatioCell, SectionCard } from "./admin-health/shared";
import { RefundTrendChart, WeeklyVolumeChart } from "./admin-health/Charts";
import { DisputeRateGauge, FunnelBars } from "./admin-health/Gauges";
import { ExperimentsSection } from "./admin-health/Experiments";

// Admin "Health" tab — activation/retention cohorts, engagement, revenue
// trends, money health, and the A/B experiment rails. Everything is computed
// live server-side (GET /admin/health); this component only renders.
//
// Money is NEVER summed across currencies — the server sends per-currency
// maps and we render them side by side (same rule as the Revenue tab).

// ---- main tab ----

export default function AdminHealthTab() {
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    adminApi
      .get("/health")
      .then((r) => setData(r.data))
      .catch(() => setError("Couldn't load health metrics. Try refreshing."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  if (!data) {
    // First load (or first load failed) — nothing to show yet.
    return (
      <section className="fade-in">
        <p className="muted-text adm-center adm-p-40">
          {loading ? (
            "Crunching health metrics…"
          ) : (
            <>
              {error || "No data."}{" "}
              <button className="adm-select adm-pointer" onClick={load}>
                Retry
              </button>
            </>
          )}
        </p>
      </section>
    );
  }

  const t = data.targets;
  const kpis = [
    { label: "Active talents (30d)", value: String(data.activeTalents30d), hint: "≥1 paid request in the last 30 days" },
    {
      label: "App opens (7d / 30d)",
      value: `${data.engagement.active7d} / ${data.engagement.active30d}`,
      hint: `of ${data.engagement.totalTalents} talents — any sign-in or app session`,
    },
    {
      label: "Requests per active talent (30d)",
      value: data.perTalent30d.requestsPerActiveTalent === null ? "—" : String(data.perTalent30d.requestsPerActiveTalent),
      hint: `${data.perTalent30d.paidRequests} paid requests`,
    },
    {
      label: "Avg request value (30d)",
      value: fmtMoneyMap(data.perTalent30d.avgValueByCurrency),
      hint: "per currency — never mixed",
    },
    {
      label: "Fan repeat rate",
      value: data.fanRepeat.pct === null ? "—" : `${data.fanRepeat.pct}%`,
      hint: `${data.fanRepeat.repeatFans} of ${data.fanRepeat.identifiableFans} identifiable fans, 2nd paid ≤30d`,
    },
    {
      label: "Payment completion (30d)",
      value: data.paymentFunnel30d.completedPct === null ? "—" : `${data.paymentFunnel30d.completedPct}%`,
      hint: `${data.paymentFunnel30d.completedPayment} of ${data.paymentFunnel30d.reachedPayment} reaching payment`,
    },
  ];

  return (
    <section className="fade-in">
      <div className="adm-mb-12" style={{ display: "flex", alignItems: "center" }}>
        <span className="muted-text adm-fs-74">
          Live from the database · computed {new Date(data.generatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
        <span className="adm-flex-1" />
        <button className="adm-select adm-pointer" onClick={load} disabled={loading}>
          {loading ? "Refreshing…" : "↻ Refresh"}
        </button>
      </div>

      {/* A failed refresh must not blank out numbers we already have. */}
      {error && (
        <div
          className="adm-mb-12 adm-fs-78" style={{
            background: "#fef2f2",
            border: "1px solid var(--adm-red)",
            borderRadius: 8,
            padding: "8px 12px",
          }}
        >
          Couldn’t refresh — showing the last loaded numbers.
        </div>
      )}

      {/* KPI tiles */}
      <div className="adm-grid-5 adm-mb-14">
        {kpis.map((k) => (
          <div key={k.label} className="kpi-card kpi-card-compact" title={k.hint}>
            <div>
              <div className="kpi-label">{k.label}</div>
              <div className="kpi-value" style={{ fontSize: k.value.length > 10 ? "0.95rem" : undefined }}>
                {k.value}
              </div>
              <div className="muted-text" style={{ fontSize: "0.64rem", marginTop: 2 }}>
                {k.hint}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Cohorts */}
      <SectionCard
        title="Activation & retention by signup month"
        note={`Activation: first paid request ≤14 days (target ${t.activationPct}%+) · Month-1: paid request in days 29–56 (${t.month1RetentionPct}%+) · Month-3: days 57–112 (${t.month3RetentionPct}%+)`}
      >
        {data.cohorts.length === 0 ? (
          <p className="muted-text">No signups yet.</p>
        ) : (
          <table className="adm-table-light adm-table-compact">
            <thead>
              <tr>
                <th>Signup month</th>
                <th>Signups</th>
                <th>Activation</th>
                <th>Month-1 retention</th>
                <th>Month-3 retention</th>
              </tr>
            </thead>
            <tbody>
              {data.cohorts.map((c) => (
                <tr key={c.month} className="hover-row">
                  <td className="adm-semibold">{fmtMonth(c.month)}</td>
                  <td>{c.signups}</td>
                  <td><RatioCell ratio={c.activation} target={t.activationPct} /></td>
                  <td><RatioCell ratio={c.month1} target={t.month1RetentionPct} /></td>
                  <td><RatioCell ratio={c.month3} target={t.month3RetentionPct} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="muted-text adm-fs-70 adm-mt-8 adm-mb-0">
          Small cohorts swing hard — one talent can move a month by 20%+, so read the “n of m” counts, not just the percentages.
          Young signups don’t count against a window until it has fully elapsed.
        </p>
      </SectionCard>

      {/* Funnel */}
      <SectionCard title="Signup → first paid request funnel" note="current state of every talent signed up in the months shown above">
        <FunnelBars funnel={data.overallFunnel} biggestDrop={data.biggestDrop} />
      </SectionCard>

      {/* Engagement / churn */}
      <SectionCard
        title="Engagement & churn watch"
        note="app activity = any successful sign-in or session refresh (tracked ~1 year back)"
      >
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 10 }}>
          <div>
            <span className="kpi-value adm-stat-num">{data.engagement.active7d}</span>{" "}
            <span className="muted-text adm-fs-76">active last 7 days</span>
          </div>
          <div>
            <span className="kpi-value adm-stat-num">{data.engagement.active30d}</span>{" "}
            <span className="muted-text adm-fs-76">active last 30 days</span>
          </div>
          <div>
            <span className="kpi-value" style={{ fontSize: "1.3rem", color: (data.engagement.churnWatchTotal ?? data.engagement.churnWatch.length) ? "var(--adm-red)" : undefined }}>
              {data.engagement.churnWatchTotal ?? data.engagement.churnWatch.length}
            </span>{" "}
            <span className="muted-text adm-fs-76">no activity in 30+ days</span>
          </div>
        </div>
        {data.engagement.churnWatch.length > 0 && (
          <details>
            <summary className="muted-text adm-fs-76 adm-pointer">
              Churn watch list ({data.engagement.churnWatchTotal ?? data.engagement.churnWatch.length}
              {(data.engagement.churnWatchTotal ?? 0) > data.engagement.churnWatch.length
                ? ` — showing the ${data.engagement.churnWatch.length} most churned`
                : ""})
            </summary>
            <table className="adm-table-light adm-table-compact adm-mt-6">
              <thead>
                <tr>
                  <th>Talent</th>
                  <th>Email</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {data.engagement.churnWatch.map((u) => (
                  <tr key={u._id}>
                    <td className="adm-semibold">{u.name}</td>
                    <td className="muted-text">{u.email}</td>
                    <td>{u.lastSeenAt ? fmtDay(u.lastSeenAt) : "never (pre-tracking)"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </SectionCard>

      {/* Weekly volume */}
      <SectionCard title="Weekly request volume (12 weeks)" note="gross paid by fans, split by currency — the last bar is the current partial week">
        <WeeklyVolumeChart weeks={data.weeklyVolume} />
      </SectionCard>

      {/* Fan payment funnel */}
      <SectionCard
        title="Fan payment funnel (30d)"
        note="starts at the payment sheet — steps before it aren't logged yet (first job for the A/B event rails)"
      >
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <div>
            <span className="kpi-value adm-stat-num">{data.paymentFunnel30d.reachedPayment}</span>{" "}
            <span className="muted-text adm-fs-76">reached payment</span>
          </div>
          <div>
            <span className="kpi-value adm-stat-num adm-green">
              {data.paymentFunnel30d.completedPayment}
            </span>{" "}
            <span className="muted-text adm-fs-76">
              completed{data.paymentFunnel30d.completedPct !== null ? ` (${data.paymentFunnel30d.completedPct}%)` : ""}
            </span>
          </div>
          <div>
            <span className="kpi-value adm-stat-num">{data.paymentFunnel30d.abandoned}</span>{" "}
            <span className="muted-text adm-fs-76">
              didn’t complete ({data.paymentFunnel30d.ofWhichExpired} expired unaccepted)
            </span>
          </div>
        </div>
      </SectionCard>

      {/* Payout incidents */}
      <SectionCard
        title="Payout incidents"
        note="read-only view of failed bank payouts Stripe reported — resolved = a later same-currency payout to that talent went through"
      >
        {data.payoutIncidents.open.length === 0 && data.payoutIncidents.resolved.length === 0 ? (
          <p className="muted-text adm-mb-0">No failed payouts. 🎉</p>
        ) : (
          <>
            {data.payoutIncidents.open.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <strong className="adm-fs-80 adm-red">
                  Open ({data.payoutIncidents.open.length})
                </strong>
                <IncidentTable incidents={data.payoutIncidents.open} />
              </div>
            )}
            {data.payoutIncidents.resolved.length > 0 && (
              <details open={data.payoutIncidents.open.length === 0}>
                <summary className="muted-text adm-fs-78 adm-pointer">
                  Resolved ({data.payoutIncidents.resolved.length})
                  {data.payoutIncidents.avgHoursToResolve !== null &&
                    ` · avg ${data.payoutIncidents.avgHoursToResolve}h to resolve`}
                </summary>
                <IncidentTable incidents={data.payoutIncidents.resolved} />
              </details>
            )}
          </>
        )}
      </SectionCard>

      {/* Dispute rate — the card-network line (fraud-door tripwires) */}
      {data.disputeRate && (
        <SectionCard
          title="Dispute rate — card-network line"
          note={`real chargebacks ÷ paid requests, last ${data.disputeRate.windowDays} days · amber ${data.disputeRate.amberPct}% · red ${data.disputeRate.redPct}%`}
        >
          <DisputeRateGauge rate={data.disputeRate} />
        </SectionCard>
      )}

      {/* Refund / dispute trend */}
      <SectionCard title="Refund & dispute rate (12 weeks)" note="refunds + disputes as % of that week's paid requests">
        <RefundTrendChart weeks={data.refundDisputeTrend} />
      </SectionCard>

      {/* Country / currency split */}
      <SectionCard title="Where talents & fans are" note="fans don't give a country — their split uses payment currency as a proxy">
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <div className="adm-flex-1" style={{ minWidth: 240 }}>
            <strong className="adm-fs-80">Talents by country</strong>
            <table className="adm-table-light adm-table-compact adm-mt-6">
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Currency</th>
                  <th>Talents</th>
                </tr>
              </thead>
              <tbody>
                {data.countrySplit.talents.map((r) => (
                  <tr key={`${r.country}-${r.currency}`}>
                    <td>{r.country}</td>
                    <td>{r.currency.toUpperCase()}</td>
                    <td>{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="adm-flex-1" style={{ minWidth: 240 }}>
            <strong className="adm-fs-80">Fan payments by currency</strong>
            <table className="adm-table-light adm-table-compact adm-mt-6">
              <thead>
                <tr>
                  <th>Currency</th>
                  <th>Paid requests</th>
                  <th>Identifiable fans</th>
                </tr>
              </thead>
              <tbody>
                {data.countrySplit.fansByCurrency.map((r) => (
                  <tr key={r.currency}>
                    <td>{r.currency.toUpperCase()}</td>
                    <td>{r.orders}</td>
                    <td>{r.identifiableFans}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </SectionCard>

      {/* Notification opt-in */}
      <SectionCard
        title="Notification opt-in (active talents)"
        note="counted only when the channel actually works: push needs a registered device, SMS needs a verified phone"
      >
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          {[
            { label: "Push", stat: data.notificationOptIn.push },
            { label: "SMS", stat: data.notificationOptIn.sms },
            { label: "Any channel", stat: data.notificationOptIn.any },
          ].map(({ label, stat }) => (
            <div key={label}>
              <span className="kpi-value adm-stat-num">
                {stat.pct === null ? "—" : `${stat.pct}%`}
              </span>{" "}
              <span className="muted-text adm-fs-76">
                {label} ({stat.count} of {data.notificationOptIn.activeTalents})
              </span>
            </div>
          ))}
        </div>
      </SectionCard>

      <ExperimentsSection />
    </section>
  );
}
